import { useState, useEffect, useMemo } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { clsx } from 'clsx'
import Header from './Header'
import Navigation from './Navigation'
import TopNavigation from './TopNavigation'
import Footer from './Footer'
import LicenseBanner from './LicenseBanner'
import AICopilotWidget from './AICopilotWidget'
import LoginAlertModal from './LoginAlertModal'
import LicenseExpiryModal from './LicenseExpiryModal'
import { useQueryClient } from '@tanstack/react-query'
import { clientNavigation, vendorNavigation, superAdminNavigation } from '../config/navigationConfig'
import { useAuth } from '../contexts/AuthContext'
import { PageActionProvider } from '../contexts/PageActionContext'
import toast from 'react-hot-toast'

export default function Layout({ children }) {
  const queryClient = useQueryClient()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { user, activePortal, isSuperAdmin } = useAuth()

  useEffect(() => {
    const handleScopeChange = () => {
      queryClient.invalidateQueries()
    }
    window.addEventListener('region-scope-changed', handleScopeChange)
    return () => window.removeEventListener('region-scope-changed', handleScopeChange)
  }, [queryClient])

  const roles = Array.isArray(user?.role) ? user.role : (user?.role ? [user.role] : []);
  const isAdmin = roles.includes('Admin') || roles.includes('SuperAdmin') || user?.type === 'SuperAdmin';
  const isVendorOnly = !isAdmin && (user?.portal_access === 'Vendor Portal' || user?.type === 'Vendor');

  const isAllowedVendorPath = (pathname) => {
    return pathname.startsWith('/vendor') || 
           pathname.startsWith('/profile') || 
           pathname.startsWith('/help') || 
           pathname.startsWith('/tickets') || 
           pathname === '/privacy-policy' || 
           pathname === '/terms-of-service';
  };

  // ⚠️ ALL hooks must be called before any conditional returns (Rules of Hooks)
  useEffect(() => {
    try {
      sessionStorage.setItem('erp:returnTo', `${location.pathname}${location.search || ''}${location.hash || ''}`)
    } catch (_) { void 0 }
  }, [location.pathname, location.search, location.hash])

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (!token || !user?.id) return

    const rawBaseURL = import.meta.env.VITE_API_URL
    const normalizedBaseURL = rawBaseURL
      ? `${rawBaseURL}`.replace(/\/+$/, '').endsWith('/api')
        ? `${rawBaseURL}`.replace(/\/+$/, '')
        : `${rawBaseURL}`.replace(/\/+$/, '') + '/api'
      : '/api'

    const streamUrl = `${normalizedBaseURL}/events/stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(streamUrl)

    const onTicketCreated = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const expected = envelope?.data?.response_time ? `${Number(envelope.data.response_time) / 60} hrs` : null
        toast.success(`Ticket #${ref} created${expected ? ` (SLA response: ${expected})` : ''}`)
      } catch (e) { void e }
    }

    const onSlaBreached = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const kind = envelope?.data?.kind === 'resolution' ? 'Resolution' : 'Response'
        toast.error(`${kind} SLA breached for Ticket #${ref}`)
      } catch (e) { void e }
    }

    const onTicketAssigned = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const level = envelope?.data?.support_level || ''
        toast(`Ticket #${ref} assigned${level ? ` (${level})` : ''}`)
      } catch (e) { void e }
    }

    const onTicketEscalated = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const toLevel = envelope?.data?.to_level || ''
        toast.error(`Ticket #${ref} escalated${toLevel ? ` to ${toLevel}` : ''}`)
      } catch (e) { void e }
    }

    const onTicketResolved = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        toast.success(`Ticket #${ref} resolved`)
      } catch (e) { void e }
    }

    es.addEventListener('ticket.created', onTicketCreated)
    es.addEventListener('ticket.assigned', onTicketAssigned)
    es.addEventListener('ticket.escalated', onTicketEscalated)
    es.addEventListener('ticket.resolved', onTicketResolved)
    es.addEventListener('sla.breached', onSlaBreached)

    return () => {
      try { es.close() } catch (_) { void 0 }
    }
  }, [user?.id])

  const isSuperAdminUser = Boolean(
    isSuperAdmin || 
    user?.type === 'SuperAdmin' || 
    activePortal === 'superadmin' ||
    (Array.isArray(user?.role) && user.role.includes('SuperAdmin')) ||
    (Array.isArray(user?.roles) && user.roles.some(r => (r.name || r) === 'SuperAdmin'))
  );

  // Select navigation based on active portal context and role
  const activeNavigation = useMemo(() => {
    if (isSuperAdminUser) {
      return superAdminNavigation
    }
    if (activePortal === 'vendor') {
      return vendorNavigation
    }
    return clientNavigation
  }, [activePortal, isSuperAdminUser])

  const isActive = (href) => {
    if (href === '/dashboard' || href === '/superadmin/control-tower' || href === '/vendor/dashboard') {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  const getPageContext = () => {
    const defaultHomeCrumb = isSuperAdminUser
      ? { label: 'Control Tower', to: '/superadmin/control-tower' }
      : (activePortal === 'vendor' ? { label: 'Home', to: '/vendor/dashboard' } : { label: 'Home', to: '/dashboard' });

    const pathname = location.pathname;
    const qs = new URLSearchParams(location.search || '');
    const tab = qs.get('tab');
    const mode = qs.get('mode');

    // ── 1. DASHBOARDS & ROOTS ───────────────────────────────────────────────────
    if (pathname === '/dashboard' || pathname === '/executive-dashboard') {
      return {
        title: 'Executive Dashboard',
        headerTitle: 'Executive Dashboard',
        description: 'Real-time Enterprise Operations & KPI Telemetry',
        breadcrumbs: [{ label: 'Home', to: '/dashboard' }]
      };
    }

    if (pathname === '/superadmin' || pathname.startsWith('/superadmin/control-tower') || pathname.startsWith('/settings/control-tower')) {
      return {
        title: 'Platform Control Tower',
        headerTitle: 'Platform Control Tower',
        description: 'SaaS Platform Operations & Infrastructure Telemetry',
        breadcrumbs: [{ label: 'Control Tower', to: '/superadmin/control-tower' }],
        fullWidth: true
      };
    }

    if (pathname === '/vendor/dashboard') {
      return {
        title: 'Vendor Portal Dashboard',
        headerTitle: 'Vendor Operations Center',
        description: 'Bids, Orders, Deliveries & Invoicing',
        breadcrumbs: [{ label: 'Home', to: '/vendor/dashboard' }],
        fullWidth: true
      };
    }

    // ── 2. SUPERADMIN PORTAL ROUTES ─────────────────────────────────────────────
    if (pathname.startsWith('/superadmin/')) {
      const baseCrumbs = [{ label: 'Control Tower', to: '/superadmin/control-tower' }];

      if (pathname === '/superadmin/clients') {
        return {
          title: 'Tenant Organizations (Sanglob Clients)',
          description: 'Tenant Provisioning & Multi-Tenant Partitioning',
          breadcrumbs: [...baseCrumbs, { label: 'Tenant Organizations', to: '/superadmin/clients' }],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/clients/new') {
        return {
          title: 'Onboard New Tenant Organization',
          description: 'Provision a New Enterprise Tenant Partition',
          breadcrumbs: [
            ...baseCrumbs,
            { label: 'Tenant Organizations', to: '/superadmin/clients' },
            { label: 'Onboard New Tenant', to: '/superadmin/clients/new' }
          ],
          fullWidth: true
        };
      }
      if (pathname.match(/^\/superadmin\/clients\/\d+\/edit$/)) {
        return {
          title: 'Configure Tenant Organization',
          description: 'Modify Tenant Profile & Tier',
          breadcrumbs: [
            ...baseCrumbs,
            { label: 'Tenant Organizations', to: '/superadmin/clients' },
            { label: 'Configure Tenant', to: pathname }
          ],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/licenses') {
        return {
          title: 'Cryptographic Licensing',
          description: 'Multi-Tenant Ed25519 License Key Generation & Sync',
          breadcrumbs: [...baseCrumbs, { label: 'Cryptographic Licensing', to: '/superadmin/licenses' }],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/roles') {
        return {
          title: 'Security & Policy Engine',
          description: 'Platform Access Control & Granular Permissions Matrix',
          breadcrumbs: [...baseCrumbs, { label: 'Security & Policy Engine', to: '/superadmin/roles' }],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/monitoring') {
        return {
          title: 'Platform Telemetry & Health',
          description: 'Real-time Node Status, Database Health & Error Monitors',
          breadcrumbs: [...baseCrumbs, { label: 'Platform Telemetry & Health', to: '/superadmin/monitoring' }],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/ai-audit') {
        return {
          title: 'Savvy AI Governance & Audit Ledger',
          description: 'Immutable AI Prompt, Reasoning & Decision Audit Trail',
          breadcrumbs: [...baseCrumbs, { label: 'Savvy AI Governance Ledger', to: '/superadmin/ai-audit' }],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/audit') {
        return {
          title: 'Immutable Audit Trail',
          description: 'Comprehensive Security & Administrative Event Log',
          breadcrumbs: [...baseCrumbs, { label: 'Immutable Audit Trail', to: '/superadmin/audit' }],
          fullWidth: true
        };
      }
      if (pathname === '/superadmin/settings') {
        return {
          title: 'System Settings',
          description: 'Global SaaS Control Plane Settings',
          breadcrumbs: [...baseCrumbs, { label: 'System Settings', to: '/superadmin/settings' }],
          fullWidth: true
        };
      }
    }

    // ── 3. BUDGET & BUDGET CONTROL ROUTES ────────────────────────────────────────
    if (pathname.startsWith('/budget') || pathname.startsWith('/budget-control')) {
      const budgetHome = { label: 'Budget Control', to: '/budget/overview' };

      if (pathname === '/budget' || pathname === '/budget/overview') {
        return {
          title: 'Budget Overview',
          description: 'Budget vs Actual Expenditure & Department Consumption',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Overview Dashboard', to: '/budget/overview' }]
        };
      }
      if (pathname === '/budget/planning') {
        return {
          title: 'Planning & Budgets',
          description: 'Departmental Allocations & Fiscal Planning',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Planning & Budgets', to: '/budget/planning' }]
        };
      }
      if (pathname === '/budget/new') {
        return {
          title: 'New Budget Plan',
          description: 'Establish Budget Allocation & Control Thresholds',
          breadcrumbs: [
            defaultHomeCrumb,
            budgetHome,
            { label: 'Planning & Budgets', to: '/budget/planning' },
            { label: 'New Budget Plan', to: '/budget/new' }
          ]
        };
      }
      if (pathname.match(/^\/budget\/\d+\/edit$/)) {
        return {
          title: 'Edit Budget',
          description: 'Update Budget Ceiling, Cost Center & Project Code',
          breadcrumbs: [
            defaultHomeCrumb,
            budgetHome,
            { label: 'Planning & Budgets', to: '/budget/planning' },
            { label: 'Edit Budget', to: pathname }
          ]
        };
      }
      if (pathname.match(/^\/budget\/\d+$/)) {
        return {
          title: 'Budget Details',
          description: 'Allocations, Commitments & Line Item Expenditures',
          breadcrumbs: [
            defaultHomeCrumb,
            budgetHome,
            { label: 'Planning & Budgets', to: '/budget/planning' },
            { label: 'Budget Details', to: pathname }
          ]
        };
      }
      if (pathname === '/budget-control/dashboard') {
        return {
          title: 'Control Matrix',
          description: 'Hard/Soft Enforcement Rules & Tolerance Limits',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Control Matrix', to: '/budget-control/dashboard' }]
        };
      }
      if (pathname === '/budget-control/rules') {
        return {
          title: 'Control Rules',
          description: 'Rule Engine Configuration & Thresholds',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Control Rules', to: '/budget-control/rules' }]
        };
      }
      if (pathname === '/budget-control/rules/new') {
        return {
          title: 'New Control Rule',
          description: 'Define Automated Enforcement Policy',
          breadcrumbs: [
            defaultHomeCrumb,
            budgetHome,
            { label: 'Control Rules', to: '/budget-control/rules' },
            { label: 'New Control Rule', to: '/budget-control/rules/new' }
          ]
        };
      }
      if (pathname.match(/^\/budget-control\/rules\/\d+$/)) {
        return {
          title: 'Rule Configuration',
          description: 'Modify Automated Enforcement Policy',
          breadcrumbs: [
            defaultHomeCrumb,
            budgetHome,
            { label: 'Control Rules', to: '/budget-control/rules' },
            { label: 'Rule Configuration', to: pathname }
          ]
        };
      }
      if (pathname === '/budget-control/overrides') {
        return {
          title: 'Budget Overrides',
          description: 'Management Exceptions & Threshold Bypasses',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Budget Overrides', to: '/budget-control/overrides' }]
        };
      }
      if (pathname === '/budget-control/commitments') {
        return {
          title: 'Commitment Register',
          description: 'Active PO Encumbrances & Reserved Funds',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Commitment Register', to: '/budget-control/commitments' }]
        };
      }
      if (pathname === '/budget-control/obligations') {
        return {
          title: 'Obligation Register',
          description: 'Contractual Obligations & Invoiced Commitments',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Obligation Register', to: '/budget-control/obligations' }]
        };
      }
      if (pathname === '/budget-control/availability-logs') {
        return {
          title: 'Availability Logs',
          description: 'Real-time Budget Checking Audit Trail',
          breadcrumbs: [defaultHomeCrumb, budgetHome, { label: 'Availability Logs', to: '/budget-control/availability-logs' }]
        };
      }
    }

    // ── 4. PROCUREMENT ROUTES ───────────────────────────────────────────────────
    if (pathname.startsWith('/purchase-orders') || pathname.startsWith('/requisitions') || pathname.startsWith('/rfx') || pathname.startsWith('/quotations') || pathname.startsWith('/order-receipts') || pathname.startsWith('/invoices') || pathname === '/contracts' || pathname === '/buy') {
      const procHome = { label: 'Procurement', to: '/buy' };

      if (pathname === '/buy') {
        return {
          title: 'Procurement Overview',
          description: 'Strategic Sourcing, Spend Tracking & Order Execution',
          breadcrumbs: [defaultHomeCrumb, { label: 'Procurement', to: '/buy' }]
        };
      }
      // Requisitions
      if (pathname === '/requisitions') {
        return {
          title: 'Requisitions',
          description: 'Internal Purchase Demands & Requests',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Requisitions', to: '/requisitions' }]
        };
      }
      if (pathname === '/requisitions/new') {
        return {
          title: 'New Requisition',
          description: 'Submit Purchase Request for Approval',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Requisitions', to: '/requisitions' }, { label: 'New Requisition', to: '/requisitions/new' }]
        };
      }
      if (pathname.match(/^\/requisitions\/\d+\/edit$/)) {
        return {
          title: 'Edit Requisition',
          description: 'Modify Purchase Demand Line Items',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Requisitions', to: '/requisitions' }, { label: 'Edit Requisition', to: pathname }]
        };
      }
      if (pathname.match(/^\/requisitions\/\d+$/)) {
        return {
          title: 'Requisition Details',
          description: 'Line Items, Quotations & Approval Progress',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Requisitions', to: '/requisitions' }, { label: 'Requisition Details', to: pathname }]
        };
      }
      // RFx / Sourcing
      if (pathname === '/rfx') {
        return {
          title: 'RFQ / Sourcing Events',
          description: 'Competitive Sourcing & Bid Packages',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'RFQ / Sourcing Events', to: '/rfx' }]
        };
      }
      if (pathname === '/rfx/new') {
        return {
          title: 'New RFQ Event',
          description: 'Publish RFQ to Approved Vendors',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'RFQ / Sourcing Events', to: '/rfx' }, { label: 'New RFQ Event', to: '/rfx/new' }]
        };
      }
      if (pathname.match(/^\/rfx\/\d+\/edit$/)) {
        return {
          title: 'Edit RFQ Event',
          description: 'Update Sourcing Scope & Terms',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'RFQ / Sourcing Events', to: '/rfx' }, { label: 'Edit RFQ Event', to: pathname }]
        };
      }
      if (pathname.match(/^\/rfx\/\d+$/)) {
        return {
          title: 'RFQ Event Details',
          description: 'Vendor Responses, Technical Evaluation & Awards',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'RFQ / Sourcing Events', to: '/rfx' }, { label: 'RFQ Details', to: pathname }]
        };
      }
      // Quotations
      if (pathname === '/quotations') {
        return {
          title: 'Vendor Quotations',
          description: 'Compare & Award Proposals',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Vendor Quotations', to: '/quotations' }]
        };
      }
      if (pathname === '/quotations/new') {
        return {
          title: 'New Quotation',
          description: 'Record Supplier Bid Submission',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Vendor Quotations', to: '/quotations' }, { label: 'New Quotation', to: '/quotations/new' }]
        };
      }
      if (pathname.match(/^\/quotations\/\d+\/edit$/)) {
        return {
          title: 'Edit Quotation',
          description: 'Modify Pricing & Commercial Terms',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Vendor Quotations', to: '/quotations' }, { label: 'Edit Quotation', to: pathname }]
        };
      }
      if (pathname.match(/^\/quotations\/\d+$/)) {
        return {
          title: 'Quotation Details',
          description: 'Pricing Breakdown & Compliance Scorecard',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Vendor Quotations', to: '/quotations' }, { label: 'Quotation Details', to: pathname }]
        };
      }
      // Purchase Orders
      if (pathname === '/purchase-orders') {
        return {
          title: 'Purchase Orders',
          description: 'Active & Historical PO Commitments',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Purchase Orders', to: '/purchase-orders' }]
        };
      }
      if (pathname === '/purchase-orders/new') {
        return {
          title: 'New Purchase Order',
          description: 'Draft & Issue Standard Purchase Order',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Purchase Orders', to: '/purchase-orders' }, { label: 'New Purchase Order', to: '/purchase-orders/new' }]
        };
      }
      if (pathname.match(/^\/purchase-orders\/\d+\/edit$/)) {
        return {
          title: 'Edit Purchase Order',
          description: 'Amend Terms & Quantities',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Purchase Orders', to: '/purchase-orders' }, { label: 'Edit Purchase Order', to: pathname }]
        };
      }
      if (pathname.match(/^\/purchase-orders\/\d+$/)) {
        return {
          title: 'Purchase Order Details',
          description: 'Fulfillment Status, Goods Receipts & Invoices',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Purchase Orders', to: '/purchase-orders' }, { label: 'PO Details', to: pathname }]
        };
      }
      // Goods Receipts (GRN)
      if (pathname === '/order-receipts') {
        return {
          title: 'Goods Receipts (GRN)',
          description: 'Warehouse Receiving & Quality Inspection',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Goods Receipts (GRN)', to: '/order-receipts' }]
        };
      }
      if (pathname === '/order-receipts/new') {
        return {
          title: 'New Goods Receipt',
          description: 'Process Delivery Note & Intake Verification',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Goods Receipts (GRN)', to: '/order-receipts' }, { label: 'New GRN', to: '/order-receipts/new' }]
        };
      }
      if (pathname.match(/^\/order-receipts\/\d+$/)) {
        return {
          title: 'Goods Receipt Details',
          description: 'Received Quantities, Batches & QC Acceptance',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Goods Receipts (GRN)', to: '/order-receipts' }, { label: 'GRN Details', to: pathname }]
        };
      }
      // Invoices
      if (pathname === '/invoices') {
        return {
          title: 'Invoices',
          description: '3-Way Match & Accounts Payable Invoices',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Invoices', to: '/invoices' }]
        };
      }
      if (pathname === '/invoices/new') {
        return {
          title: 'New Invoice',
          description: 'Create AP Invoice with Multi-Tax Verification',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Invoices', to: '/invoices' }, { label: 'New Invoice', to: '/invoices/new' }]
        };
      }
      if (pathname.match(/^\/invoices\/\d+\/edit$/)) {
        return {
          title: 'Edit Invoice',
          description: 'Modify Billing Info & Tax Breakdown',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Invoices', to: '/invoices' }, { label: 'Edit Invoice', to: pathname }]
        };
      }
      if (pathname.match(/^\/invoices\/\d+$/)) {
        return {
          title: 'Invoice Details',
          description: '3-Way Match Status, Tax Audit & Payment Schedule',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Invoices', to: '/invoices' }, { label: 'Invoice Details', to: pathname }]
        };
      }
      if (pathname === '/contracts') {
        return {
          title: 'Contracts',
          description: 'Master Vendor Agreements & SLA Terms',
          breadcrumbs: [defaultHomeCrumb, procHome, { label: 'Contracts', to: '/contracts' }]
        };
      }
    }

    // ── 5. FINANCE & ACCOUNTING ROUTES ──────────────────────────────────────────
    if (pathname.startsWith('/finance')) {
      const finHome = { label: 'Finance', to: '/finance' };

      if (pathname === '/finance') {
        return {
          title: 'Finance Overview',
          description: 'Financial Statements, Cash Flow & General Ledger',
          breadcrumbs: [defaultHomeCrumb, finHome]
        };
      }
      if (pathname.startsWith('/finance/accounting/general-ledger')) {
        const glHome = { label: 'General Ledger', to: '/finance/accounting/general-ledger' };
        if (pathname === '/finance/accounting/general-ledger/new') {
          return {
            title: 'New Journal Entry',
            description: 'Double-Entry Balanced Debit & Credit Journal',
            breadcrumbs: [defaultHomeCrumb, finHome, glHome, { label: 'New Journal Entry', to: pathname }]
          };
        }
        if (pathname === '/finance/accounting/general-ledger/accounts/new') {
          return {
            title: 'New Chart of Account',
            description: 'Define Sub-Account & Financial Category',
            breadcrumbs: [defaultHomeCrumb, finHome, glHome, { label: 'New Account', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/general-ledger\/\d+\/edit$/)) {
          return {
            title: 'Edit Journal Entry',
            description: 'Modify Journal Line Items',
            breadcrumbs: [defaultHomeCrumb, finHome, glHome, { label: 'Edit Journal', to: pathname }]
          };
        }
        return {
          title: 'General Ledger',
          description: 'Chart of Accounts, Journal Entries & Trial Balance',
          breadcrumbs: [defaultHomeCrumb, finHome, glHome]
        };
      }
      if (pathname.startsWith('/finance/accounting/accounts-payable')) {
        const apHome = { label: 'Accounts Payable', to: '/finance/accounting/accounts-payable' };
        if (pathname === '/finance/accounting/accounts-payable/new') {
          return {
            title: 'New AP Entry',
            description: 'Book Vendor Liability & Aging Record',
            breadcrumbs: [defaultHomeCrumb, finHome, apHome, { label: 'New AP Entry', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/accounts-payable\/\d+\/edit$/)) {
          return {
            title: 'Edit AP Entry',
            description: 'Update Payable Terms & Schedule',
            breadcrumbs: [defaultHomeCrumb, finHome, apHome, { label: 'Edit AP Entry', to: pathname }]
          };
        }
        return {
          title: 'Accounts Payable',
          description: 'Vendor Invoices, Payment Due Dates & Aging Schedule',
          breadcrumbs: [defaultHomeCrumb, finHome, apHome]
        };
      }
      if (pathname.startsWith('/finance/accounting/accounts-receivable')) {
        const arHome = { label: 'Accounts Receivable', to: '/finance/accounting/accounts-receivable' };
        if (pathname === '/finance/accounting/accounts-receivable/new') {
          return {
            title: 'New AR Invoice',
            description: 'Generate Outgoing Customer Invoice',
            breadcrumbs: [defaultHomeCrumb, finHome, arHome, { label: 'New AR Invoice', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/accounts-receivable\/\d+\/edit$/)) {
          return {
            title: 'Edit AR Invoice',
            description: 'Modify Receivable Amounts & Terms',
            breadcrumbs: [defaultHomeCrumb, finHome, arHome, { label: 'Edit AR Invoice', to: pathname }]
          };
        }
        return {
          title: 'Accounts Receivable',
          description: 'Customer Invoices, Collections & Aging',
          breadcrumbs: [defaultHomeCrumb, finHome, arHome]
        };
      }
      if (pathname.startsWith('/finance/accounting/cash-bank')) {
        const cbHome = { label: 'Cash & Banking', to: '/finance/accounting/cash-bank' };
        if (pathname === '/finance/accounting/cash-bank/new') {
          return {
            title: 'New Bank Transaction',
            description: 'Record Bank Inflow / Outflow',
            breadcrumbs: [defaultHomeCrumb, finHome, cbHome, { label: 'New Transaction', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/cash-bank\/\d+\/edit$/)) {
          return {
            title: 'Edit Bank Record',
            description: 'Update Bank Account Details',
            breadcrumbs: [defaultHomeCrumb, finHome, cbHome, { label: 'Edit Bank Record', to: pathname }]
          };
        }
        return {
          title: 'Cash & Banking',
          description: 'Treasury Accounts, Bank Feeds & Reconciliation',
          breadcrumbs: [defaultHomeCrumb, finHome, cbHome]
        };
      }
      if (pathname.startsWith('/finance/accounting/payments')) {
        const payHome = { label: 'Disbursements', to: '/finance/accounting/payments' };
        if (pathname === '/finance/accounting/payments/new') {
          return {
            title: 'New Payment Voucher',
            description: 'Issue Vendor Payment & Remittance',
            breadcrumbs: [defaultHomeCrumb, finHome, payHome, { label: 'New Payment', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/payments\/\d+\/edit$/)) {
          return {
            title: 'Edit Payment Voucher',
            description: 'Update Disbursement Information',
            breadcrumbs: [defaultHomeCrumb, finHome, payHome, { label: 'Edit Payment', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/payments\/\d+$/)) {
          return {
            title: 'Payment Details',
            description: 'Settlement Status & Multi-Currency Breakdown',
            breadcrumbs: [defaultHomeCrumb, finHome, payHome, { label: 'Payment Details', to: pathname }]
          };
        }
        return {
          title: 'Disbursements',
          description: 'Vendor Payments, Bank Transfers & Settlement Log',
          breadcrumbs: [defaultHomeCrumb, finHome, payHome]
        };
      }
      if (pathname.startsWith('/finance/accounting/tax')) {
        const taxHome = { label: 'Tax Compliance', to: '/finance/accounting/tax' };
        if (pathname === '/finance/accounting/tax/new') {
          return {
            title: 'New Tax Entry',
            description: 'Record Tax Liability or Credit',
            breadcrumbs: [defaultHomeCrumb, finHome, taxHome, { label: 'New Tax Entry', to: pathname }]
          };
        }
        if (pathname.match(/^\/finance\/accounting\/tax\/\d+\/edit$/)) {
          return {
            title: 'Edit Tax Entry',
            description: 'Update Statutory Filing Details',
            breadcrumbs: [defaultHomeCrumb, finHome, taxHome, { label: 'Edit Tax Entry', to: pathname }]
          };
        }
        return {
          title: 'Tax Compliance',
          description: 'GST/VAT Ledgers, Withholding Tax & Filings',
          breadcrumbs: [defaultHomeCrumb, finHome, taxHome]
        };
      }
    }

    // ── 6. INVENTORY & POS ROUTES ───────────────────────────────────────────────
    if (pathname.startsWith('/inventory') || pathname.startsWith('/pos')) {
      const invHome = { label: 'Inventory & POS', to: '/inventory' };

      if (pathname === '/inventory') {
        return {
          title: 'Inventory Stock',
          description: 'Real-time Stock Levels, Valuation & Reorder Points',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'Inventory Stock', to: '/inventory' }]
        };
      }
      if (pathname === '/inventory/new') {
        return {
          title: 'New Inventory Item',
          description: 'Catalog New SKU & Safety Stock Parameters',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'Inventory Stock', to: '/inventory' }, { label: 'New Inventory Item', to: '/inventory/new' }]
        };
      }
      if (pathname.match(/^\/inventory\/\d+\/edit$/)) {
        return {
          title: 'Edit Inventory Item',
          description: 'Update SKU Attributes & Costing Method',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'Inventory Stock', to: '/inventory' }, { label: 'Edit Item', to: pathname }]
        };
      }
      if (pathname.match(/^\/inventory\/\d+$/)) {
        return {
          title: 'Inventory Item Details',
          description: 'Stock History, Batch Lots & Movements',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'Inventory Stock', to: '/inventory' }, { label: 'Item Details', to: pathname }]
        };
      }
      if (pathname === '/pos' || pathname === '/pos/dashboard' || pathname === '/pos/register' || pathname === '/pos/terminals') {
        return {
          title: 'POS Terminals & Register',
          description: 'Point of Sale Billing, Cash Drawers & Receipts',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'POS Terminals & Register', to: '/pos' }]
        };
      }
      if (pathname === '/pos/branches-stores') {
        return {
          title: 'Branches & Stores',
          description: 'Multi-Location Warehouses & Retail Outlets',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'Branches & Stores', to: '/pos/branches-stores' }]
        };
      }
      if (pathname === '/pos/transfers') {
        return {
          title: 'Stock Transfers',
          description: 'Inter-Branch Stock Movement & Manifests',
          breadcrumbs: [defaultHomeCrumb, invHome, { label: 'Stock Transfers', to: '/pos/transfers' }]
        };
      }
    }

    // ── 7. REPORTS & ANALYTICS ROUTES ───────────────────────────────────────────
    if (pathname.startsWith('/reports')) {
      const repHome = { label: 'Reports & Analytics', to: '/reports' };
      if (pathname === '/reports') {
        return {
          title: 'Report Hub',
          description: 'Unified Executive & Operational Reporting Suite',
          breadcrumbs: [defaultHomeCrumb, repHome, { label: 'Report Hub', to: '/reports' }]
        };
      }
      // Dynamic report /reports/:category/:slug
      const parts = pathname.split('/').filter(Boolean);
      const reportSlug = parts[2] || parts[1] || 'Report';
      const formattedTitle = reportSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return {
        title: formattedTitle,
        description: 'Comprehensive Analytics & Data Export',
        breadcrumbs: [defaultHomeCrumb, repHome, { label: 'Report Hub', to: '/reports' }, { label: formattedTitle, to: pathname }]
      };
    }

    // ── 8. APPROVALS ROUTES ─────────────────────────────────────────────────────
    if (pathname.startsWith('/approvals')) {
      const appHome = { label: 'Approvals', to: '/approvals' };
      if (pathname === '/approvals/inbox') {
        return {
          title: 'Approval Inbox',
          description: 'Items Requiring Your Authorization',
          breadcrumbs: [defaultHomeCrumb, appHome, { label: 'Approval Inbox', to: '/approvals/inbox' }]
        };
      }
      if (pathname === '/approvals/workflows') {
        return {
          title: 'Workflow Rules Engine',
          description: 'Hierarchy & Threshold Approval Matrix',
          breadcrumbs: [defaultHomeCrumb, appHome, { label: 'Workflow Rules Engine', to: '/approvals/workflows' }]
        };
      }
      return {
        title: 'Approval Center',
        description: 'All Department Workflow Status & Tracking',
        breadcrumbs: [defaultHomeCrumb, appHome, { label: 'Approval Center', to: '/approvals' }]
      };
    }

    // ── 9. SETTINGS & SYSTEM CONFIGURATION ──────────────────────────────────────
    if (pathname.startsWith('/settings')) {
      const setHome = { label: 'Settings', to: '/settings/system' };

      if (pathname === '/settings/business-configuration') {
        return {
          title: 'Company & Org Structure',
          description: 'Departments, Cost Centers, Project Codes & Units',
          breadcrumbs: [defaultHomeCrumb, setHome, { label: 'Company & Org Structure', to: '/settings/business-configuration' }]
        };
      }
      if (pathname === '/settings/license') {
        return {
          title: 'License & Subscription',
          description: 'Cryptographic License Sync & Entitlements',
          breadcrumbs: [defaultHomeCrumb, setHome, { label: 'License & Subscription', to: '/settings/license' }]
        };
      }
      if (pathname === '/settings/billing') {
        return {
          title: 'Billing & Usage',
          description: 'Tenant Quota Consumption & Invoices',
          breadcrumbs: [defaultHomeCrumb, setHome, { label: 'Billing & Usage', to: '/settings/billing' }]
        };
      }

      // Tabbed System Settings
      const tabTitles = {
        users: 'User Management & Roles',
        currencies: 'Multi-Currency Configuration',
        tax_rules: 'Global Tax Rules Engine',
        departments: 'Organization & Master Data',
        alerts: 'Alert & Threshold Rules',
        security: 'Security & Access Controls',
        audit: 'Audit Logs & Telemetry'
      };

      if (tab) {
        const tabTitle = tabTitles[tab] || 'System Configuration';
        if (tab === 'users' && mode === 'new') {
          return {
            title: 'New User',
            description: 'Add Personnel & Assign ERP Roles',
            breadcrumbs: [
              defaultHomeCrumb,
              setHome,
              { label: 'User Management', to: '/settings/system?tab=users' },
              { label: 'New User', to: '/settings/system?tab=users&mode=new' }
            ]
          };
        }
        return {
          title: tabTitle,
          description: 'Tenant System & Business Settings',
          breadcrumbs: [defaultHomeCrumb, setHome, { label: tabTitle, to: `/settings/system?tab=${tab}` }]
        };
      }

      return {
        title: 'System Configuration',
        description: 'Tenant Currencies, Tax Rules, Departments & Master Data',
        breadcrumbs: [defaultHomeCrumb, setHome, { label: 'System Configuration', to: '/settings/system' }]
      };
    }

    // ── 10. VENDOR PORTAL SUBPATHS ──────────────────────────────────────────────
    if (activePortal === 'vendor' || pathname.startsWith('/vendor') || pathname.startsWith('/vendor-pos')) {
      const vHome = { label: 'Home', to: '/vendor/dashboard' };

      if (pathname === '/vendor/profile') {
        return { title: 'Company Profile', description: 'Vendor Tax IDs & Banking Info', breadcrumbs: [vHome, { label: 'Company Profile', to: '/vendor/profile' }], fullWidth: true };
      }
      if (pathname === '/vendor/rfx') {
        return { title: 'RFQ / Sourcing Events', description: 'Open Bidding Packages', breadcrumbs: [vHome, { label: 'RFQ / Sourcing Events', to: '/vendor/rfx' }], fullWidth: true };
      }
      if (pathname.match(/^\/vendor\/rfx\/\d+$/)) {
        return { title: 'RFQ Details', description: 'Sourcing Package & Technical Scope', breadcrumbs: [vHome, { label: 'RFQ / Sourcing Events', to: '/vendor/rfx' }, { label: 'RFQ Details', to: pathname }], fullWidth: true };
      }
      if (pathname === '/vendor/bids') {
        return { title: 'Quotations / Bids', description: 'Submitted Proposals & Commercial Offers', breadcrumbs: [vHome, { label: 'Quotations / Bids', to: '/vendor/bids' }], fullWidth: true };
      }
      if (pathname === '/vendor-pos') {
        return { title: 'Purchase Orders', description: 'Customer Orders Awaiting Fulfillment', breadcrumbs: [vHome, { label: 'Purchase Orders', to: '/vendor-pos' }], fullWidth: true };
      }
      if (pathname.match(/^\/vendor-pos\/\d+$/)) {
        return { title: 'Purchase Order Details', description: 'Quantities, Schedules & Line Items', breadcrumbs: [vHome, { label: 'Purchase Orders', to: '/vendor-pos' }, { label: 'PO Details', to: pathname }], fullWidth: true };
      }
      if (pathname.match(/^\/vendor-pos\/\d+\/receive$/)) {
        return { title: 'Delivery Confirmation', description: 'Confirm Warehouse Receipt', breadcrumbs: [vHome, { label: 'Purchase Orders', to: '/vendor-pos' }, { label: 'Delivery Confirmation', to: pathname }], fullWidth: true };
      }
      if (pathname === '/vendor/shipments') {
        return { title: 'Deliveries & Shipments', description: 'Outbound Dispatches & Logistics', breadcrumbs: [vHome, { label: 'Deliveries & Shipments', to: '/vendor/shipments' }], fullWidth: true };
      }
      if (pathname.startsWith('/vendor/finance/invoices')) {
        const invH = { label: 'Invoices', to: '/vendor/finance/invoices' };
        if (pathname === '/vendor/finance/invoices/new') {
          return { title: 'Submit Invoice', description: 'Upload Tax Invoice for Settlement', breadcrumbs: [vHome, invH, { label: 'Submit Invoice', to: pathname }], fullWidth: true };
        }
        if (pathname.match(/^\/vendor\/finance\/invoices\/\d+$/)) {
          return { title: 'Invoice Details', description: '3-Way Match & Approval Status', breadcrumbs: [vHome, invH, { label: 'Invoice Details', to: pathname }], fullWidth: true };
        }
        return { title: 'Invoices', description: 'Billing Status & Settlement Tracking', breadcrumbs: [vHome, invH], fullWidth: true };
      }
      if (pathname.startsWith('/vendor/finance/payments')) {
        const payH = { label: 'Payment Status', to: '/vendor/finance/payments' };
        if (pathname.match(/^\/vendor\/finance\/payments\/\d+$/)) {
          return { title: 'Payment Details', description: 'Remittance Slip & Advice', breadcrumbs: [vHome, payH, { label: 'Payment Details', to: pathname }], fullWidth: true };
        }
        return { title: 'Payment Status', description: 'Received Disbursements & Remittance', breadcrumbs: [vHome, payH], fullWidth: true };
      }
      if (pathname === '/vendor/reports') {
        return { title: 'Vendor Reports', description: 'Fulfillment Metrics & Scorecards', breadcrumbs: [vHome, { label: 'Vendor Reports', to: '/vendor/reports' }], fullWidth: true };
      }
      if (pathname === '/vendor/support') {
        return { title: 'Vendor Support', description: 'Help Desk & Inquiry Tickets', breadcrumbs: [vHome, { label: 'Vendor Support', to: '/vendor/support' }], fullWidth: true };
      }
    }

    // ── 11. COMMON APPLICATION PAGES ────────────────────────────────────────────
    if (pathname === '/profile') {
      return { title: 'User Profile', description: 'Security Credentials & Preferences', breadcrumbs: [defaultHomeCrumb, { label: 'User Profile', to: '/profile' }] };
    }
    if (pathname === '/usage-logs') {
      return { title: 'Usage Logs', description: 'PO and License Quota Activity', breadcrumbs: [defaultHomeCrumb, { label: 'Usage Logs', to: '/usage-logs' }] };
    }
    if (pathname === '/analytics') {
      return { title: 'Analytics', description: 'Operational Intelligence & Business Metrics', breadcrumbs: [defaultHomeCrumb, { label: 'Analytics', to: '/analytics' }] };
    }
    if (pathname === '/grc') {
      return { title: 'Governance, Risk & Compliance (GRC)', description: 'Risk Matrix & Regulatory Compliance Policies', breadcrumbs: [defaultHomeCrumb, { label: 'GRC', to: '/grc' }] };
    }
    if (pathname === '/ai') {
      return { title: 'Savvy AI Intelligence Studio', description: 'Conversational Enterprise Autonomous Agent', breadcrumbs: [defaultHomeCrumb, { label: 'AI Intelligence Studio', to: '/ai' }] };
    }
    if (pathname === '/privacy-policy') {
      return { title: 'Privacy Policy', description: 'Data Protection & Compliance', breadcrumbs: [defaultHomeCrumb, { label: 'Privacy Policy', to: '/privacy-policy' }] };
    }
    if (pathname === '/terms-of-service') {
      return { title: 'Terms of Service', description: 'Contractual Usage Agreement', breadcrumbs: [defaultHomeCrumb, { label: 'Terms of Service', to: '/terms-of-service' }] };
    }

    // ── FALLBACK ────────────────────────────────────────────────────────────────
    return {
      title: isSuperAdminUser ? 'Platform Control Tower' : 'Dashboard',
      headerTitle: isSuperAdminUser ? 'Platform Control Tower' : 'Procurement Overview',
      description: '',
      breadcrumbs: [defaultHomeCrumb]
    };
  }

  const pageContext = getPageContext()
  const isSuperAdminRoute = location.pathname.startsWith('/superadmin') || location.pathname.startsWith('/settings/control-tower')
  const isVendorPortal = activePortal === 'vendor' || location.pathname.startsWith('/vendor') || location.pathname.startsWith('/vendor-pos')
  const isFullWidth = Boolean(pageContext.fullWidth) || isSuperAdminRoute || isVendorPortal
  const isSettingsRoute = location.pathname.startsWith('/settings') || isSuperAdminRoute
  const shouldNormalizeFont = isSettingsRoute || !isVendorPortal

  // Vendor-only users must stay in vendor paths — redirect AFTER all hooks are called
  if (isVendorOnly && !isAllowedVendorPath(location.pathname)) {
    return <Navigate to="/vendor/dashboard" replace />;
  }

  return (
    <PageActionProvider>
      <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
        {/* Global License Warning Banner */}
        <LicenseBanner />
        
        {/* Top Navigation Bar (NetSuite Style) */}
        <TopNavigation onMobileMenuOpen={() => setSidebarOpen(true)} fullWidth={isFullWidth} />

        {/* Mobile Navigation Sidebar (Drawer) */}
        <Navigation 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          isCollapsed={false}
          toggleCollapse={() => {}}
          mobileOnly={true}
        />

        {/* Main Content Area */}
        <div className="flex flex-col flex-1 overflow-hidden relative z-0">
          {/* Page Context Header (Only on sub-pages, not primary dashboard since Dashboard is in top navbar) */}
          {location.pathname !== '/dashboard' && (
            <Header 
              breadcrumbs={pageContext.breadcrumbs}
              title={pageContext.title}
              description={pageContext.description}
              showDescriptionBelow={pageContext.showDescriptionBelow}
              fullWidth={isFullWidth}
            />
          )}

          {/* Scrollable Content */}
          <main
            className={clsx(
              'flex-1 py-6 overflow-y-auto stylish-scrollbar',
              shouldNormalizeFont ? 'text-sm' : null,
              isFullWidth ? 'px-2 sm:px-4 lg:px-6' : 'px-4 sm:px-6 lg:px-8'
            )}
          >
            <div className="w-full">
              {children}
            </div>
          </main>

          <Footer fullWidth={isFullWidth} />
        </div>

        {/* Global Floating AI Copilot (Voice STT, TTS, RBAC Actions) */}
        <AICopilotWidget />

        {/* Real-time Login Critical Alert Center Modal */}
        <LoginAlertModal />

        {/* Real-time Post-Login License Expiry Alert Modal */}
        <LicenseExpiryModal />
      </div>
    </PageActionProvider>
  )
}
